import { useState, useCallback, useRef, useEffect } from "react";
import { Plus, Pencil, Trash2, X, Loader2, Landmark } from "lucide-react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useFinance, BankAccountData } from "./modal/FinanceContext";
import { createBankAccount, updateBankAccount, deleteBankAccount } from "./api/financeApi";

const bankSchema = Yup.object().shape({
    account_name: Yup.string().required("Account name is required"),
    bank_name: Yup.string().required("Bank name is required"),
    account_number: Yup.string().required("Account number is required"),
    branch_code: Yup.string(),
    iban_number: Yup.string(),
    opening_balance: Yup.number().min(0, "Opening balance cannot be negative"),
});

const fmt = (n?: number) =>
    (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const inputStyles =
    "w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all";
const labelStyles = "block text-xs font-medium text-muted-foreground mb-1 font-inter";

interface BankFormModalProps {
    editItem: BankAccountData | null;
    onClose: () => void;
    onSaved: () => void;
}

const BankFormModal = ({ editItem, onClose, onSaved }: BankFormModalProps) => {
    const [apiError, setApiError] = useState("");
    const overlayRef = useRef<HTMLDivElement>(null);

    const formik = useFormik({
        initialValues: {
            account_name: editItem?.account_name || "",
            bank_name: editItem?.bank_name || "",
            account_number: editItem?.account_number || "",
            branch_code: editItem?.branch_code || "",
            iban_number: editItem?.iban_number || "",
            opening_balance: editItem?.opening_balance ?? 0,
        },
        validationSchema: bankSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setApiError("");
            try {
                const payload: Record<string, any> = {
                    account_name: values.account_name,
                    bank_name: values.bank_name,
                    account_number: values.account_number,
                    branch_code: values.branch_code || undefined,
                    iban_number: values.iban_number || undefined,
                };
                if (!editItem) {
                    payload.opening_balance = parseFloat(String(values.opening_balance || 0));
                }
                if (editItem) {
                    await updateBankAccount(parseInt(editItem.id || "0"), payload);
                } else {
                    await createBankAccount(payload);
                }
                onSaved();
            } catch (err: any) {
                const detail = err.response?.data?.detail;
                setApiError(
                    typeof detail === "string"
                        ? detail
                        : Array.isArray(detail)
                        ? detail.map((e: any) => e.msg).join(", ")
                        : "Failed to save bank account"
                );
            }
        },
    });

    return (
        <div
            ref={overlayRef}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
        >
            <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-base font-semibold text-foreground font-inter">
                        {editItem ? "Edit Bank Account" : "Add Bank Account"}
                    </h2>
                    <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-surface-muted">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <form onSubmit={formik.handleSubmit} noValidate className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelStyles}>Account Name *</label>
                            <input
                                name="account_name"
                                value={formik.values.account_name}
                                onChange={formik.handleChange}
                                placeholder="Main Operations"
                                className={inputStyles}
                            />
                            {formik.errors.account_name && formik.touched.account_name && (
                                <p className="text-destructive text-xs mt-1 font-inter">{formik.errors.account_name}</p>
                            )}
                        </div>
                        <div>
                            <label className={labelStyles}>Bank Name *</label>
                            <input
                                name="bank_name"
                                value={formik.values.bank_name}
                                onChange={formik.handleChange}
                                placeholder="HBL"
                                className={inputStyles}
                            />
                            {formik.errors.bank_name && formik.touched.bank_name && (
                                <p className="text-destructive text-xs mt-1 font-inter">{formik.errors.bank_name}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelStyles}>Account Number *</label>
                            <input
                                name="account_number"
                                value={formik.values.account_number}
                                onChange={formik.handleChange}
                                placeholder="0123456789"
                                className={inputStyles}
                            />
                            {formik.errors.account_number && formik.touched.account_number && (
                                <p className="text-destructive text-xs mt-1 font-inter">{formik.errors.account_number}</p>
                            )}
                        </div>
                        <div>
                            <label className={labelStyles}>Branch Code (optional)</label>
                            <input
                                name="branch_code"
                                value={formik.values.branch_code}
                                onChange={formik.handleChange}
                                placeholder="0478"
                                className={inputStyles}
                            />
                        </div>
                    </div>

                    <div>
                        <label className={labelStyles}>IBAN Number (optional)</label>
                        <input
                            name="iban_number"
                            value={formik.values.iban_number}
                            onChange={formik.handleChange}
                            placeholder="PK00HBLA0000000000000000"
                            className={inputStyles}
                        />
                    </div>

                    <div>
                        <label className={labelStyles}>Opening Balance</label>
                        <input
                            name="opening_balance"
                            type="number"
                            value={formik.values.opening_balance}
                            onChange={formik.handleChange}
                            placeholder="0"
                            className={inputStyles}
                            disabled={!!editItem}
                        />
                        {editItem && (
                            <p className="text-xs text-muted-foreground font-inter mt-1">Opening balance cannot be changed after creation.</p>
                        )}
                        {formik.errors.opening_balance && formik.touched.opening_balance && (
                            <p className="text-destructive text-xs mt-1 font-inter">{formik.errors.opening_balance as string}</p>
                        )}
                    </div>

                    {apiError && (
                        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                            <p className="text-destructive text-sm font-inter">{apiError}</p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={formik.isSubmitting || (!!editItem && !formik.dirty)}
                            className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors flex items-center gap-2"
                        >
                            {formik.isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editItem ? "Update" : "Add Account"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const BankAccounts = () => {
    const { bankAccountsList, loadBankAccounts } = useFinance();
    const [showForm, setShowForm] = useState(false);
    const [editItem, setEditItem] = useState<BankAccountData | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<BankAccountData | null>(null);
    const [deleteError, setDeleteError] = useState("");
    const [deleting, setDeleting] = useState(false);
    const deleteOverlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaved = useCallback(async () => {
        await loadBankAccounts();
        setShowForm(false);
        setEditItem(null);
    }, [loadBankAccounts]);

    const handleEdit = (account: BankAccountData) => {
        setEditItem(account);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditItem(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await deleteBankAccount(parseInt(deleteTarget.id || "0"));
            await loadBankAccounts();
            setDeleteTarget(null);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setDeleteError(typeof detail === "string" ? detail : "Failed to delete bank account");
        }
        setDeleting(false);
    };

    const thClass = "py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter";
    const tdClass = "py-4 px-4 text-sm text-foreground font-inter";

    return (
        <>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground font-inter">Bank Accounts</h1>
                    <p className="text-sm text-muted-foreground font-inter mt-1">Manage company bank accounts and track balances</p>
                </div>
                <button
                    onClick={() => { setEditItem(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add Account
                </button>
            </div>

            {bankAccountsList.length === 0 ? (
                <div className="bg-surface border border-border rounded-2xl p-12 text-center">
                    <Landmark className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground font-inter text-sm">No bank accounts yet. Add one to get started.</p>
                </div>
            ) : (
                <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-surface-muted/50">
                                <tr className="border-b border-border">
                                    <th className={thClass}>Account Name</th>
                                    <th className={`${thClass} hidden sm:table-cell`}>Bank</th>
                                    <th className={`${thClass} hidden md:table-cell`}>Account Number</th>
                                    <th className={`${thClass} hidden xl:table-cell`}>Branch Code</th>
                                    <th className={`${thClass} hidden xl:table-cell`}>Opening Balance</th>
                                    <th className={`${thClass} hidden lg:table-cell`}>Income</th>
                                    <th className={`${thClass} hidden lg:table-cell`}>Expense</th>
                                    <th className={thClass}>Current Balance</th>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bankAccountsList.map((account, i) => {
                                    const balance = account.current_balance ?? 0;
                                    const balancePositive = balance >= 0;
                                    return (
                                        <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-muted/30 transition-colors">
                                            <td className={tdClass}>
                                                <div className="font-medium text-foreground">{account.account_name}</div>
                                                {account.iban_number && (
                                                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">{account.iban_number}</div>
                                                )}
                                            </td>
                                            <td className={`${tdClass} hidden sm:table-cell`}>{account.bank_name}</td>
                                            <td className={`${tdClass} hidden md:table-cell font-mono`}>{account.account_number}</td>
                                            <td className={`${tdClass} hidden xl:table-cell font-mono`}>{account.branch_code || "—"}</td>
                                            <td className={`${tdClass} hidden xl:table-cell`}>{fmt(account.opening_balance)}</td>
                                            <td className={`${tdClass} hidden lg:table-cell text-emerald-400`}>{fmt(account.total_income)}</td>
                                            <td className={`${tdClass} hidden lg:table-cell text-red-400`}>{fmt(account.total_expense)}</td>
                                            <td className={tdClass}>
                                                <span className={`font-semibold ${balancePositive ? "text-emerald-400" : "text-red-400"}`}>
                                                    {balancePositive ? "" : "-"}{fmt(Math.abs(balance))}
                                                </span>
                                            </td>
                                            <td className={`${tdClass} text-right`}>
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(account)}
                                                        className="p-1.5 text-muted-foreground hover:text-primary hover:bg-surface-muted rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => { setDeleteTarget(account); setDeleteError(""); }}
                                                        className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showForm && (
                <BankFormModal
                    editItem={editItem}
                    onClose={handleCloseForm}
                    onSaved={handleSaved}
                />
            )}

            {deleteTarget && (
                <div
                    ref={deleteOverlayRef}
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === deleteOverlayRef.current) setDeleteTarget(null); }}
                >
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-sm p-6 shadow-2xl">
                        <h2 className="text-base font-semibold text-foreground font-inter mb-2">Delete Bank Account</h2>
                        <p className="text-sm text-muted-foreground font-inter mb-1">
                            Are you sure you want to delete <span className="text-foreground font-medium">{deleteTarget.account_name}</span>?
                        </p>
                        <p className="text-xs text-muted-foreground font-inter mb-5">
                            This cannot be undone. Accounts linked to finance records cannot be deleted.
                        </p>
                        {deleteError && (
                            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                                <p className="text-destructive text-sm font-inter">{deleteError}</p>
                            </div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="px-5 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-destructive-foreground text-sm font-medium font-inter rounded-xl transition-colors flex items-center gap-2"
                            >
                                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BankAccounts;
